import { createFileRoute, useParams } from '@tanstack/react-router'
import { trpc } from '@/utils/trpc'
import { Button } from '@/components/ui/button'
import { useMutation, useQuery } from '@tanstack/react-query'
import { CheckCircle2, Calendar, CreditCard, AlertCircle, Receipt, XCircle } from 'lucide-react'
import { motion } from 'framer-motion'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"

export const Route = createFileRoute('/subscription/$subscription_id')({
  component: SubscriptionDetails,
})

function SubscriptionDetails() {
  const {subscription_id} = useParams({ from: '/subscription/$subscription_id' })
  
  // Get subscription details
  const subscriptionDetails = useQuery(trpc.donation.checkSubscription.queryOptions({
    subscriptionId: subscription_id,
  }))

  // Cancel subscription
  const cancelSubscription = useMutation(trpc.donation.cancelSubscription.mutationOptions({
    onSuccess: () => {
      window.location.href = '/canceled'
    },
    onError: (error) => {
      console.error("Error cancelling subscription:", error)
    },
  }))

  // Loading state
  if (subscriptionDetails.isLoading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500"></div>
      </div>
    )
  }

  // Error state
  if (subscriptionDetails.isError) {
    const isRateLimitError = subscriptionDetails.error.message.includes('För många försök');
    const isInvalidFormatError = subscriptionDetails.error.message.includes('Ogiltigt format');
    
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="text-center p-6 rounded-lg bg-red-50 text-red-800">
          <AlertCircle className="w-12 h-12 mx-auto mb-4" />
          <h2 className="text-xl font-semibold mb-2">Ett fel uppstod</h2>
          <p className="mb-4">
            {isInvalidFormatError 
              ? "Ogiltigt format på prenumerations-ID"
              : subscriptionDetails.error.message}
          </p>
          {isRateLimitError && (
            <p className="text-sm text-gray-600">
              Försök igen om 5 minuter eller kontakta support om problemet kvarstår.
            </p>
          )}
        </div>
      </div>
    )
  }

  // No subscription details found
  if (!subscriptionDetails.data) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="text-center p-6 rounded-lg bg-yellow-50 text-yellow-800">
          <AlertCircle className="w-12 h-12 mx-auto mb-4" />
          <h2 className="text-xl font-semibold mb-2">Ingen gåva hittades</h2>
        </div>
      </div>
    )
  }

  // Subscription details
  const { amount, currency, currentPeriodEnd, paymentHistory, status } = subscriptionDetails.data
  const isActive = status === 'active'

  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="container mx-auto max-w-2xl p-6"
    >
      <div className="text-center mb-8">
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.2, type: "spring" }}
        >
          {isActive ? (
            <CheckCircle2 className="w-20 h-20 text-green-500 mx-auto mb-4" />
          ) : (
            <XCircle className="w-20 h-20 text-gray-400 mx-auto mb-4" />
          )}
        </motion.div>
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          {isActive ? 'Din Månadsvisa Gåva' : 'Avslutad Månadsvis Gåva'}
        </h1>
        <p className="text-gray-600">
          {isActive 
            ? 'Hantera din månadsvisa gåva och se betalningshistorik'
            : 'Se historik för din tidigare månadsvisa gåva'}
        </p>
      </div>

      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="bg-white rounded-2xl shadow-lg p-6 mb-8"
      >
        <h2 className="text-xl font-semibold text-gray-900 mb-6">Gåvoinformation</h2>
        
        <div className="space-y-6">
          <div className="flex items-center p-4 bg-gray-50 rounded-xl">
            <CreditCard className="w-6 h-6 text-green-500 mr-4" />
            <div>
              <p className="text-sm text-gray-600">Månadsbelopp</p>
              <p className="text-lg font-semibold text-gray-900">{amount} {currency.toUpperCase()}/månad</p>
            </div>
          </div>

          {isActive && (
            <div className="flex items-center p-4 bg-gray-50 rounded-xl">
              <Calendar className="w-6 h-6 text-green-500 mr-4" />
              <div>
                <p className="text-sm text-gray-600">Nästa betalning</p>
                <p className="text-lg font-semibold text-gray-900">
                  {new Date(currentPeriodEnd * 1000).toLocaleDateString('sv-SE', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </p>
              </div>
            </div>
          )}
        </div>
      </motion.div>

      {paymentHistory && paymentHistory.length > 0 && (
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-white rounded-2xl shadow-lg p-6 mb-8"
        >
          <h2 className="text-xl font-semibold text-gray-900 mb-6">Betalningshistorik</h2>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-left">Datum</TableHead>
                  <TableHead className="text-left">Belopp</TableHead>
                  <TableHead className="text-left">Status</TableHead>
                  <TableHead className="text-left">Kvitto</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {paymentHistory.map((payment) => (
                  <TableRow key={payment.id}>
                    <TableCell>
                      {new Date(payment.date * 1000).toLocaleDateString('sv-SE', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </TableCell>
                    <TableCell>
                      {payment.amount} {payment.currency.toUpperCase()}
                    </TableCell>
                    <TableCell>
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        Betald
                      </span>
                    </TableCell>
                    <TableCell>
                      {payment.receiptUrl && (
                        <a
                          href={payment.receiptUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center text-sm text-green-600 hover:text-green-700"
                        >
                          <Receipt className="w-4 h-4 mr-1" />
                          Visa kvitto
                        </a>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </motion.div>
      )}

      {isActive && (
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="flex justify-center mb-8"
        >
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="destructive">
                Avbryt Månadsvis Gåva
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Är du säker?</AlertDialogTitle>
                <AlertDialogDescription>
                  Detta kommer att avbryta din månadsvisa gåva. Du kommer inte att debiteras igen.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Avbryt</AlertDialogCancel>
                <AlertDialogAction
                  onClick={() => cancelSubscription.mutate({ subscriptionId: subscription_id })}
                >
                  Bekräfta
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </motion.div>
      )}

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.6 }}
        className="text-center"
      >
        <Button
          variant="outline"
          onClick={() => window.location.href = '/'}
          className="hover:bg-green-50 hover:text-green-600 hover:border-green-200 transition-colors"
        >
          Tillbaka till Startsidan
        </Button>
      </motion.div>
    </motion.div>
  )
} 